/**
 * Service Firebase per la Vista Agenda Globale (PR8a).
 *
 * Strategia di query (D2 approvata):
 *  1) collectionGroup('appuntamenti') filtrato per intervallo data.
 *     Niente filtri server-side per dietologa/tipo/stato: i filtri
 *     vengono applicati lato client perche' Firestore non combina
 *     IN + range + orderBy in modo efficiente su collectionGroup.
 *  2) Dedup pazienteId (estratto da `doc.ref.parent.parent.id`,
 *     subcollection /pazienti/{pid}/appuntamenti/{aid}).
 *  3) Batch getDocs pazienti in chunk da 30 (limite Firestore `in`).
 *
 * Il pazienteId NON e' un campo del documento appuntamento (PR7):
 * va sempre derivato dal path di Firestore.
 *
 * Indici richiesti (presenti in `firestore.indexes.json` PR8a):
 *  - collectionGroup appuntamenti, (data ASC, dietologaId ASC)
 *  - collectionGroup appuntamenti, (data ASC, stato ASC)
 *  - single-field automatico su `data` (creato da Firestore).
 */

import {
  collectionGroup,
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  Timestamp,
  type Query,
} from 'firebase/firestore';
import { startOfDay } from 'date-fns';
import { firestore } from '../security/firebase';
import {
  vincoloVisibilitaAppuntamenti,
  vincoloVisibilitaPazienti,
} from './visibilita';
import type { Appuntamento } from '@shared/types/appuntamento';
import type { Paziente } from '@shared/types/paziente';

/**
 * Documento appuntamento arricchito col proprio `id` e `pazienteId`
 * (derivato dal path Firestore).
 */
export interface AppuntamentoRecord extends Appuntamento {
  id: string;
  pazienteId: string;
}

/**
 * Carica tutti gli appuntamenti la cui `data` cade in
 * `[startDate, endDate)`. La query e' su collectionGroup ed e'
 * ordinata per data ASC.
 *
 * PR-E — own-only DIETOLOGA: se `scopeDietologaId` e' valorizzato, la query
 * aggiunge `where('dietologaId','==', scopeDietologaId)`. Questo e'
 * OBBLIGATORIO per la DIETOLOGA: senza il where le Firestore Rules
 * (collectionGroup) rifiutano la list (nessun leak studio-wide). Richiede
 * l'index collection-group [dietologaId ASC, data ASC] (firestore.indexes).
 * TITOLARE/SEGRETERIA/ADMIN passano `undefined` -> vista studio-wide.
 *
 * Il campo `data` su Firestore puo' essere `Timestamp` (creato via
 * `FieldValue.serverTimestamp` o per le history) oppure stringa ISO
 * 8601 (creato dal client tramite CF `createAppuntamento`). La query
 * usa `Timestamp` per il range filter: Firestore confronta correttamente
 * Timestamp con Timestamp, mentre con stringhe ISO 8601 in confronto
 * lessicografico funziona ma con caveat (vedi nota sotto).
 *
 * NOTA: nei test fake-firestore i confronti sono numerici. In runtime
 * reale, `data` salvato dalla CF e' una stringa ISO 8601. Firestore
 * confronta lessicograficamente le stringhe e in formato ISO 8601 la
 * comparazione lessicografica coincide con la cronologica.
 */
export async function listAppuntamentiSettimana(
  startDate: Date,
  endDate: Date,
  isBackoffice: boolean,
  scopeDietologaId?: string
): Promise<AppuntamentoRecord[]> {
  // Usiamo stringhe ISO 8601 perche' il campo `data` su Firestore e'
  // stringa ISO (vedi createAppuntamento). Confronto lessicografico
  // su ISO 8601 coincide con cronologico.
  const startIso = startDate.toISOString();
  const endIso = endDate.toISOString();

  // PR-E: il where('dietologaId','==',...) va prima del range su `data`
  // (equality-first). Index collection-group [dietologaId ASC, data ASC].
  const constraints = [
    ...vincoloVisibilitaAppuntamenti(isBackoffice),
    ...(scopeDietologaId
      ? [where('dietologaId', '==', scopeDietologaId)]
      : []),
    where('data', '>=', startIso),
    where('data', '<', endIso),
    orderBy('data', 'asc'),
  ];
  const q = query(
    collectionGroup(firestore, 'appuntamenti'),
    ...constraints
  ) as Query;
  const snap = await getDocs(q);

  return snap.docs.map((d) => {
    // Path: /pazienti/{pazienteId}/appuntamenti/{appuntamentoId}.
    // ref.parent = collection 'appuntamenti'; ref.parent.parent = doc paziente.
    const pazienteRef = d.ref.parent.parent;
    const pazienteId = pazienteRef?.id ?? '';
    const raw = d.data() as Appuntamento;
    return {
      id: d.id,
      pazienteId,
      ...raw,
    };
  });
}

/**
 * Direzione dell'appuntamento di riferimento rispetto ad "oggi":
 *  - 'prossimo': primo appuntamento con `data >= startOfDay(oggi)` (futuro
 *    o odierno), ordinato ASC.
 *  - 'ultimo': fallback quando non esistono appuntamenti futuri/odierni;
 *    e' l'appuntamento passato piu' recente (`data < startOfDay(oggi)`),
 *    ordinato DESC.
 */
export type DirezioneRiferimento = 'prossimo' | 'ultimo';

export interface AppuntamentoRiferimento {
  record: AppuntamentoRecord;
  direzione: DirezioneRiferimento;
}

/**
 * Appuntamento di riferimento di un singolo paziente per la ricerca in
 * Agenda (patient-first): dato il `pazienteId`, ritorna il prossimo
 * appuntamento futuro/odierno; se assente, il piu' recente passato.
 *
 * GDPR: interroga ESCLUSIVAMENTE la subcollection calendario
 * `/pazienti/{pid}/appuntamenti` (mai `appuntamenti_clinici`, mai
 * collectionGroup cross-paziente). Lo scoping e' per path (un solo paziente).
 *
 * RBAC — own-only DIETOLOGA (replica INLINE del predicato PR-E): se
 * `scopeDietologaId` e' valorizzato la query aggiunge
 * `where('dietologaId','==',scopeDietologaId)`, coerente con l'agenda scoped
 * che l'utente vedra' dopo la navigazione. Gli altri ruoli passano
 * `undefined`. L'enforcement vero resta nelle Firestore Rules
 * (`isAssignedDietologa` sulla subcollection).
 *
 * Boundary a `startOfDay(now)`: gli appuntamenti di oggi restano 'prossimo'.
 *
 * Indici (COLLECTION scope su `appuntamenti`):
 *  - future scoped:   [dietologaId ASC, data ASC]  (gia' presente)
 *  - past scoped:     [dietologaId ASC, data DESC] (aggiunto in firestore.indexes.json)
 *  - future/past non scoped: single-field `data` ASC/DESC (fieldOverrides).
 */
export async function getAppuntamentoRiferimento(
  pazienteId: string,
  isBackoffice: boolean,
  scopeDietologaId?: string,
  now: Date = new Date()
): Promise<AppuntamentoRiferimento | null> {
  if (!pazienteId) return null;

  const boundaryIso = startOfDay(now).toISOString();
  const appuntamentiRef = collection(
    firestore,
    'pazienti',
    pazienteId,
    'appuntamenti'
  );
  const scopeConstraint = [
    ...vincoloVisibilitaAppuntamenti(isBackoffice),
    ...(scopeDietologaId
      ? [where('dietologaId', '==', scopeDietologaId)]
      : []),
  ];

  // 1) Prossimo futuro/odierno (equality-first: lo scope precede il range).
  const futureQ = query(
    appuntamentiRef,
    ...scopeConstraint,
    where('data', '>=', boundaryIso),
    orderBy('data', 'asc'),
    limit(1)
  ) as Query;
  const futureSnap = await getDocs(futureQ);
  if (!futureSnap.empty) {
    const d = futureSnap.docs[0];
    return {
      record: { id: d.id, pazienteId, ...(d.data() as Appuntamento) },
      direzione: 'prossimo',
    };
  }

  // 2) Fallback: piu' recente passato.
  const pastQ = query(
    appuntamentiRef,
    ...scopeConstraint,
    where('data', '<', boundaryIso),
    orderBy('data', 'desc'),
    limit(1)
  ) as Query;
  const pastSnap = await getDocs(pastQ);
  if (!pastSnap.empty) {
    const d = pastSnap.docs[0];
    return {
      record: { id: d.id, pazienteId, ...(d.data() as Appuntamento) },
      direzione: 'ultimo',
    };
  }

  return null;
}

/**
 * Variante che ritorna un Timestamp anche per il campo `data` salvato
 * come ISO string (fallback). Utility per future migrazioni.
 */
export function parseAppuntamentoDate(value: Date | string | Timestamp): Date {
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  return new Date(value);
}

/**
 * Batch getDocs di pazienti per id, in chunk da 30 (limite Firestore
 * `where(documentId(), 'in', [...])`).
 *
 * Ritorna una Map<id, Paziente> per fast lookup; gli id non trovati
 * sono semplicemente assenti dalla mappa (consumer puo' fallback su
 * placeholder "(paziente sconosciuto)").
 */
export async function getPazientiBatch(
  ids: ReadonlyArray<string>,
  isBackoffice = false
): Promise<Map<string, Paziente>> {
  const out = new Map<string, Paziente>();
  if (ids.length === 0) return out;

  const dedup = Array.from(new Set(ids)).filter((x) => x.length > 0);
  const CHUNK_SIZE = 30;
  const chunks: string[][] = [];
  for (let i = 0; i < dedup.length; i += CHUNK_SIZE) {
    chunks.push(dedup.slice(i, i + CHUNK_SIZE));
  }

  for (const chunk of chunks) {
    const q = query(
      collection(firestore, 'pazienti'),
      ...vincoloVisibilitaPazienti(isBackoffice),
      where(documentId(), 'in', chunk)
    ) as Query;
    try {
      const snap = await getDocs(q);
      for (const d of snap.docs) {
        out.set(d.id, d.data() as Paziente);
      }
    } catch (e) {
      if (!isPermissionDenied(e)) throw e;
      for (const [id, paz] of await leggiPazientiUnoAUno(chunk)) {
        out.set(id, paz);
      }
    }
  }

  return out;
}

function isPermissionDenied(e: unknown): boolean {
  return (e as { code?: string } | null)?.code === 'permission-denied';
}

/**
 * Fallback della lettura a lotti quando le Rules rifiutano il lotto.
 *
 * Su `where(documentId(),'in',[...])` le Rules NON si accontentano del
 * vincolo `soloBackoffice == false`: valutano il permesso documento per
 * documento, e basta un id nascosto (o inesistente: `resource` nullo fa
 * fallire `visibileNonBackoffice()`) perche' l'intero lotto venga
 * rifiutato. Senza questo fallback un solo id storto fa fallire l'agenda
 * intera, appuntamenti compresi.
 *
 * Qui leggiamo i pazienti singolarmente e scartiamo quelli negati: il
 * nome resta vuoto e la UI mostra il placeholder, ma l'agenda regge.
 */
async function leggiPazientiUnoAUno(
  ids: readonly string[]
): Promise<Array<[string, Paziente]>> {
  const esiti = await Promise.all(
    ids.map(async (id): Promise<[string, Paziente] | null> => {
      try {
        const snap = await getDoc(doc(firestore, 'pazienti', id));
        return snap.exists() ? [id, snap.data() as Paziente] : null;
      } catch (e) {
        if (!isPermissionDenied(e)) throw e;
        return null;
      }
    })
  );
  return esiti.filter((x): x is [string, Paziente] => x !== null);
}
